/**
 * MethodologyPanelDialog.tsx
 * Phase 5, Item 6 — Panel dialog for browsing and editing methodology blocks.
 *
 * Opened via the "Methodology" button in the TestCatalogPage toolbar.
 * Lists all active catalog items; each row shows its current block status
 * (version + heading) and an "Edit" button that opens the per-item MethodologyDialog.
 *
 * All API calls go through testCatalogService (apiClient-backed).
 * Colors use theme tokens only — no hex literals.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import { Search, Edit, Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { MethodologyDialog } from './MethodologyDialog';
import { getMethodologyBlock } from '../../../services/testCatalogService';
import type { ProposalMethodologyBlock } from '../../../types/schedule';
import type { TestCatalogItem } from '../../../types/testCatalogItem';

interface Props {
  open: boolean;
  onClose: () => void;
  allItems: TestCatalogItem[];
}

interface BlockStatus {
  loaded: boolean;
  block: ProposalMethodologyBlock | null;
}

export const MethodologyPanelDialog: React.FC<Props> = ({
  open,
  onClose,
  allItems,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  // Map: TestCatalogItemKey → block status
  const [statuses, setStatuses] = useState<Map<string, BlockStatus>>(new Map());
  // Which item's methodology dialog is open
  const [editingItemKey, setEditingItemKey] = useState<string | null>(null);
  const [editingItemName, setEditingItemName] = useState<string>('');

  const loadStatuses = useCallback(async () => {
    if (allItems.length === 0) return;
    setLoadingStatuses(true);
    try {
      const entries = await Promise.all(
        allItems.map(async (item) => {
          try {
            const block = await getMethodologyBlock(item.TestCatalogItemKey);
            return [item.TestCatalogItemKey, { loaded: true, block }] as const;
          } catch {
            return [item.TestCatalogItemKey, { loaded: true, block: null }] as const;
          }
        }),
      );
      setStatuses(new Map(entries));
    } finally {
      setLoadingStatuses(false);
    }
  }, [allItems]);

  useEffect(() => {
    if (open) {
      loadStatuses();
      setSearchQuery('');
    } else {
      setStatuses(new Map());
      setEditingItemKey(null);
      setEditingItemName('');
    }
  }, [open, loadStatuses]);

  const filteredItems = allItems.filter((item) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      item.TestName.toLowerCase().includes(q) ||
      (item.Category ?? '').toLowerCase().includes(q)
    );
  });

  const handleEdit = (item: TestCatalogItem) => {
    setEditingItemKey(item.TestCatalogItemKey);
    setEditingItemName(item.TestName);
  };

  const handleMethodologyDialogClose = async () => {
    // Refresh status for the item that was just edited
    if (editingItemKey) {
      try {
        const block = await getMethodologyBlock(editingItemKey);
        setStatuses((prev) => {
          const next = new Map(prev);
          next.set(editingItemKey, { loaded: true, block });
          return next;
        });
      } catch { /* ignore — status will be stale but that's fine */ }
    }
    setEditingItemKey(null);
    setEditingItemName('');
  };

  return (
    <>
      <Dialog open={open && !editingItemKey} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Manage Methodology Blocks
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tests by name or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loadingStatuses ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <List dense disablePadding>
              {filteredItems.map((item, idx) => {
                const status = statuses.get(item.TestCatalogItemKey);
                const block = status?.block ?? null;

                return (
                  <React.Fragment key={item.TestCatalogItemKey}>
                    {idx > 0 && <Divider />}
                    <ListItem
                      sx={{
                        '&:hover': { bgcolor: theme.table.hoverBg },
                        py: 1,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.TestName}
                            </Typography>
                            {item.Category && (
                              <Chip
                                label={item.Category}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 10 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          block ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.25 }}
                            >
                              Version {block.Version}
                              {block.Heading ? ` — ${block.Heading}` : ''}
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ fontStyle: 'italic', display: 'block', mt: 0.25 }}
                            >
                              No methodology block
                            </Typography>
                          )
                        }
                      />
                      <ListItemSecondaryAction>
                        {block && (
                          <Chip
                            label={`v${block.Version}`}
                            size="small"
                            sx={{
                              bgcolor: theme.schedule.committed,
                              color: '#fff',
                              fontSize: 10,
                              mr: 1,
                            }}
                          />
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(item)}
                          title={block ? 'Edit methodology block' : 'Add methodology block'}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                );
              })}
              {filteredItems.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No tests match your search.
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Per-item methodology edit dialog */}
      {editingItemKey && (
        <MethodologyDialog
          open={!!editingItemKey}
          onClose={handleMethodologyDialogClose}
          testCatalogItemKey={editingItemKey}
          testName={editingItemName}
        />
      )}
    </>
  );
};
